export type CareerStageCode = "STARTING_OUT" | "EMERGING" | "PROFESSIONAL";
export type RevenueBandCode = "REV_5K_20K" | "REV_20K_50K" | "REV_50K_PLUS";

export interface CareerStageOption {
  value: CareerStageCode;
  label: string;
  subLabel: string;
}

export interface AnnualSalesOption {
  value: RevenueBandCode;
  label: string;
}

export const CAREER_STAGES: readonly CareerStageOption[] = [
  { value: "STARTING_OUT", label: "Starting out", subLabel: "Early career" },
  { value: "EMERGING", label: "Emerging", subLabel: "Building momentum" },
  { value: "PROFESSIONAL", label: "Professional", subLabel: "Established practice" },
] as const;

export const ANNUAL_SALES: readonly AnnualSalesOption[] = [
  { value: "REV_5K_20K", label: "5K – 20K" },
  { value: "REV_20K_50K", label: "20K – 50K" },
  { value: "REV_50K_PLUS", label: "50K+" },
] as const;

export const DEFAULT_SALES_BY_STAGE: Record<CareerStageCode, RevenueBandCode> = {
  STARTING_OUT: "REV_5K_20K",
  EMERGING: "REV_20K_50K",
  PROFESSIONAL: "REV_50K_PLUS",
};

export type ProfileEventName =
  | "onboarding_profile_viewed"
  | "onboarding_profile_continued";

export const ONBOARDING_PROFILE_VIEWED: ProfileEventName = "onboarding_profile_viewed";
export const ONBOARDING_PROFILE_CONTINUED: ProfileEventName = "onboarding_profile_continued";

export interface NameValidationResult {
  ok: boolean;
  msg?: string;
}

// Digits are allowed anywhere, including first: studio names like "180 Studio"
// and "3 Standard Sardine" are as ordinary as "Studio 54".
const NAME_RE = /^[\p{L}\p{N}][\p{L}\p{N}\s\-']*$/u;

export function validateNameLength(v: string): NameValidationResult {
  const t = (v || "").trim();
  if (t.length < 2) return { ok: false, msg: "Must be at least 2 characters" };
  if (t.length > 100) return { ok: false, msg: "Must be 100 characters or fewer" };
  return { ok: true };
}

export function validateName(v: string): NameValidationResult {
  const t = (v || "").trim();
  if (!t) return { ok: false, msg: "Required" };
  const length = validateNameLength(t);
  if (!length.ok) return length;
  if (!NAME_RE.test(t)) return { ok: false, msg: "Letters, numbers, spaces, hyphens and apostrophes only" };
  return { ok: true };
}

// CreateAccountScreen.tsx (the legacy, Mantine-backed create-account screen) already has its own
// EMAIL_REGEX, identical to this one. This is a second copy rather than a shared import - that
// file is outside this ticket's cut. Unifying the two is a follow-up, not part of this change.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(v: string): boolean {
  return EMAIL_RE.test((v || "").trim());
}
